import { useCallback, useEffect, useMemo } from 'react';
import keys from 'lodash/keys';
import { toast } from 'react-toastify';

import type { IMotoboyPlaceDedication } from '@/Interfaces/Data';
import Requests from '@/Services/Requests';
import useRequest from '../util/useRequest';
import type { IPlaceMinimal } from '../place/usePlacesMinimal';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import {
  motoboyDedicationRequest,
  selectDedication,
  selectLastMotoboyDedication,
} from '@/_store/modules/motoboy-info';

const useMotoboyDedication = (motoboyId?: string) => {
  const dispatch = useAppDispatch();

  const dedication = useAppSelector(selectDedication);
  const errors = useAppSelector(state => state.motoboyInfo.dedicationErrors);
  const loading = useAppSelector(state => state.motoboyInfo.dedicationFetching);

  const lastMotoboyIdDedication = useAppSelector(selectLastMotoboyDedication);

  const updateDedicationState = useRequest<IMotoboyPlaceDedication>();

  const deleteDedicationState = useRequest<boolean>();

  const placesDedicated = useMemo(() => {
    if (!dedication) {
      return [];
    }

    return keys(dedication);
  }, [dedication]);

  useEffect(() => {
    if (motoboyId && motoboyId !== lastMotoboyIdDedication) {
      loadDedicationData();
    }
  }, [motoboyId]);

  const getDedicationByPlaceId = useCallback(
    (placeId: string) => {
      if (!dedication) {
        return null;
      }

      const placeDedicationKey = placesDedicated.find(key => key === placeId);

      if (!placeDedicationKey) {
        return null;
      }

      return dedication[placeDedicationKey];
    },
    [placesDedicated],
  );

  function loadDedicationData() {
    if (motoboyId) {
      dispatch(motoboyDedicationRequest(motoboyId));
      return true;
    }

    if (lastMotoboyIdDedication) {
      dispatch(motoboyDedicationRequest(lastMotoboyIdDedication));
      return true;
    }

    return false;
  }

  async function handleUpdateDedicationByPlaceId(
    placeId: string,
    data: IMotoboyPlaceDedication,
  ) {
    const motoboy = motoboyId || lastMotoboyIdDedication;

    if (!motoboy) {
      return false;
    }

    const response = await updateDedicationState.request(
      Requests.motoboys.updateDedicationByPlace(motoboy, placeId, data),
    );

    return response?.data;
  }

  async function handleDeleteDedicationByPlaceId(place: IPlaceMinimal) {
    const motoboy = motoboyId || lastMotoboyIdDedication;

    if (!motoboy) {
      return false;
    }
    try {
      await updateDedicationState.request(
        Requests.motoboys.deleteDedicationByPlace(motoboy, place._id),
      );

      toast.success(
        `Dedicação do estabelecimento ${place.nomeExibicao} excluída!`,
      );
      loadDedicationData();

      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.warn(
        deleteDedicationState.error || 'Não foi possível deletar a dedicação!',
      );
    }
  }

  return {
    dedication,
    loadDedicationData,
    placesDedicated,
    dedicationErrors: errors,
    dedicationIsLoading: loading,
    getDedicationByPlaceId,
    lastMotoboyIdDedication,

    handleUpdateDedicationByPlaceId,
    updateDedicationState: {
      isLoading: updateDedicationState.isLoading,
      errors: updateDedicationState.error,
      data: updateDedicationState.data,
    },

    handleDeleteDedicationByPlaceId,
    deleteDedicationState: {
      isLoading: deleteDedicationState.isLoading,
      errors: deleteDedicationState.error,
      data: deleteDedicationState.data,
    },
  };
};

export default useMotoboyDedication;
